import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PenLine } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { api } from "@/lib/api";
import { useGoogleLinks } from "./GoogleReviews";
import { Lightbox, RatingSummary, ReviewCard } from "./ReviewBits";

export const GuestStories = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const google = useGoogleLinks();
  const writeHref = google.write || "/reviews#write";
  useEffect(() => { api.get("/reviews").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  if (!data.reviews.length) return (
    <section className="container-x pt-24" data-testid="home-guest-stories-empty">
      <div className="rounded-3xl border border-sky-100 bg-mist p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <p className="eyebrow">Customer reviews</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-semibold">Travelled with Ocean Wings Travel?</h2>
          <p className="mt-2 text-slate-600">Share your experience on Google, or read what other travellers say on our Reviews page.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/reviews" data-testid="guest-stories-empty-all" className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-300 bg-white text-navy font-semibold hover:border-ocean hover:text-ocean transition-colors">Read reviews</Link>
        <a href={writeHref} target="_blank" rel="noreferrer" data-testid="guest-stories-empty-write" className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-ocean text-white font-semibold hover:bg-navy transition-colors"><PenLine className="h-4 w-4" />Write a review</a>
        </div>
      </div>
    </section>
  );
  const featured = data.reviews.filter((r) => r.featured);
  const list = featured.length ? featured : data.reviews.slice(0, 8);

  return (
    <section className="bg-mist mt-24 py-20 sm:py-24" data-testid="home-guest-stories">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Guest reviews</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">Loved by travellers to the Andamans</h2>
            <div className="mt-4"><RatingSummary data={data} /></div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/reviews" data-testid="guest-stories-all-link" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-ocean text-white text-sm font-semibold hover:bg-navy transition-colors">Read all reviews <ArrowRight className="h-4 w-4" /></Link>
            <a href={writeHref} target="_blank" rel="noreferrer" data-testid="guest-stories-share-link" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border border-slate-300 bg-white text-navy text-sm font-semibold hover:border-ocean hover:text-ocean transition-colors"><PenLine className="h-4 w-4" />Write a review</a>
          </div>
        </div>
        <Carousel opts={{ align: "start", loop: list.length > 3 }} className="mt-10" data-testid="guest-review-slider">
          <CarouselContent>
            {list.map((r) => (
              <CarouselItem key={r.id} className="basis-[88%] sm:basis-1/2 lg:basis-1/3">
                <ReviewCard r={r} onPhoto={setOpen} className="h-full" />
              </CarouselItem>
            ))}
          </CarouselContent>
          {list.length > 1 && (
            <div className="mt-6 flex justify-end gap-2">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 bg-white" data-testid="guest-slider-prev" />
              <CarouselNext className="static translate-y-0 h-10 w-10 bg-white" data-testid="guest-slider-next" />
            </div>
          )}
        </Carousel>
      </div>
      <Lightbox item={open} onClose={() => setOpen(null)} />
    </section>
  );
};
